import React from "react";
import dayjs from "dayjs";
import { GET_TEAM_SCORECARD } from "./graphql";
import { Card, Kpi, ReportState, SortTable, PersonButton, useReport, scoreClass, fmt, rupees } from "./ui";

// One score per professional, its parts, and the change from the period
// just before.

const Score = ({ v, big }) => <span className={`${scoreClass(v)} ${big ? "font-semibold text-base" : ""}`}>{v === null || v === undefined ? "—" : Math.round(v)}</span>;

const Change = ({ v }) => {
  if (v === null || v === undefined) return <span className="text-gray-300">—</span>;
  if (Math.abs(v) < 1) return <span className="text-gray-400">±0</span>;
  return <span className={v > 0 ? "text-emerald-600" : "text-red-600"}>{v > 0 ? `▲ ${Math.round(v)}` : `▼ ${Math.round(-v)}`}</span>;
};

// What each part is made of, in words, for the tooltip.
const detail = (key, r) =>
  ({
    rating: r.ratings ? `${r.avgRating}★ from ${r.ratings} ratings, ${r.lowRatings} at 3★ or less` : "Not enough ratings",
    punctuality: r.timed ? `${r.onTime} of ${r.timed} started on time` : "No timed starts",
    reliability: `${r.declined} declined, ${r.cancelledByPro} cancelled of ${r.assigned} assigned`,
    appDiscipline: `${r.cleanFlow} of ${r.completed} with every step tapped as it happened`,
    attendance: `${r.absent} days absent without leave`,
    cash: r.unsettledBookings ? `${r.unsettledBookings} bookings unsettled, oldest ${r.oldestUnsettledDays} days` : "No cash pending",
    audits: r.auditCycles ? `${r.auditCycles - r.auditsMissed} of ${r.auditCycles} audit cycles done` : "No audits due",
  }[key]);

const ScorecardTab = ({ filter, onPick }) => {
  const { data, loading, error } = useReport(GET_TEAM_SCORECARD, "getTeamScorecard", filter);
  const parts = data?.parts || [];
  const ranked = (data?.employees || []).filter((e) => e.rank);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];
  const fallers = ranked.filter((e) => e.change !== null && e.change <= -10);

  const columns = [
    { key: "rank", label: "Rank", cell: (e) => (e.rank ? `#${e.rank}` : <span className="text-xs text-gray-400" title={`Fewer than ${data.minCompleted} completed bookings`}>not ranked</span>), sort: (e) => (e.rank ? -e.rank : -999) },
    { key: "overall", label: "Score", cell: (e) => <Score v={e.overall} big />, sort: (e) => e.overall },
    { key: "change", label: "vs before", title: "Change from the same number of days just before", cell: (e) => <Change v={e.change} />, sort: (e) => e.change },
    ...parts.map((p) => ({
      key: p.key,
      label: p.label,
      title: `${p.hint} · weight ${p.weight}%`,
      cell: (e) => (
        <span title={detail(p.key, e.raw)}>
          <Score v={e.parts[p.key]} />
        </span>
      ),
      sort: (e) => e.parts[p.key],
    })),
    { key: "completed", label: "Done", title: "Completed bookings", cell: (e) => fmt(e.raw.completed), sort: (e) => e.raw.completed },
    { key: "revenue", label: "Revenue", cell: (e) => rupees(e.raw.revenue), sort: (e) => e.raw.revenue },
  ];

  return (
    <ReportState loading={loading} error={error} data={data}>
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Team score" value={data.team.overall === null ? "—" : Math.round(data.team.overall)} valueClass={scoreClass(data.team.overall)} sub={`average of ${data.team.ranked} ranked professionals`} />
            <Kpi label="Top" value={best ? Math.round(best.overall) : "—"} valueClass="text-emerald-600" sub={best?.name} />
            <Kpi label="Bottom" value={worst ? Math.round(worst.overall) : "—"} valueClass={scoreClass(worst?.overall)} sub={worst?.name} />
            <Kpi label="Dropped 10+ points" value={fallers.length} valueClass={fallers.length ? "text-red-600" : "text-gray-900"} sub={fallers.map((f) => f.name).join(", ") || "vs the period before"} />
          </div>

          <Card title="Team average by part" hint="Where the team as a whole loses points.">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {parts.map((p) => (
                <div key={p.key} className="rounded-lg border border-gray-100 p-3" title={p.hint}>
                  <p className="text-xs text-gray-500">{p.label}</p>
                  <p className={`text-2xl font-semibold tabular-nums ${scoreClass(data.team.parts[p.key])}`}>{data.team.parts[p.key] === null ? "—" : Math.round(data.team.parts[p.key])}</p>
                  <p className="text-[11px] text-gray-400">weight {p.weight}%</p>
                </div>
              ))}
            </div>
          </Card>

          <Card
            title="Scorecard"
            hint={`Hover a score for what it's made of. Compared with ${dayjs(data.previous.startDate).format("D MMM")} – ${dayjs(data.previous.endDate).format("D MMM")}. Fewer than ${data.minCompleted} completed bookings: shown, not ranked.`}
          >
            <SortTable
              columns={columns}
              rows={data.employees}
              initialSort={{ key: "rank", dir: -1 }}
              rowKey={(e) => e.employeeId}
              nameCell={{ label: "Professional", cell: (e) => <PersonButton row={e} onPick={onPick} /> }}
              rowClass={(e) => (e.rank ? "" : "opacity-60")}
            />
          </Card>
        </>
      )}
    </ReportState>
  );
};

export default ScorecardTab;
